import { WasteManifestSource } from "generated-graphql/graphql";

import { DOCUMENT_CHIP_HEX, SOURCE_DOT_HEX } from "./sourceColors";

/** Canonical display order for the three manifest data pillars. */
export const SOURCE_ORDER: readonly WasteManifestSource[] = [
  WasteManifestSource.EManifest,
  WasteManifestSource.Vendor,
  WasteManifestSource.ManifestDocument,
];

export const SOURCE_LABEL: Record<WasteManifestSource, string> = {
  [WasteManifestSource.EManifest]: "e-Manifest",
  [WasteManifestSource.Vendor]: "Vendor",
  [WasteManifestSource.ManifestDocument]: "Document",
};

/**
 * The "Document" source CHIP color: a violet-blue (indigo[500]), high-contrast as
 * a chip label. Deliberately NOT orange — orange is reserved across the app for
 * staff-only affordances (e.g. the edit pencil). The Data Sources chip reads this;
 * the dots use the brighter {@link SOURCE_COLOR} below. Value lives in
 * {@link DOCUMENT_CHIP_HEX} (shared with the M3 compare dialog).
 */
export const DOCUMENT_SOURCE_COLOR = DOCUMENT_CHIP_HEX;

/**
 * Per-field source DOT colors — the bright/neon accent of each source's hue, so
 * the tiny 8px dots are easy to tell apart at that size (the darker chip shades
 * blurred together as dots). Distinct from the muted chip colors by design.
 * Values come from {@link SOURCE_DOT_HEX}, shared with the M3 compare dialog so
 * the two definitions can't drift.
 */
export const SOURCE_COLOR: Record<WasteManifestSource, string> = {
  [WasteManifestSource.EManifest]: SOURCE_DOT_HEX.eManifest,
  [WasteManifestSource.Vendor]: SOURCE_DOT_HEX.vendor,
  [WasteManifestSource.ManifestDocument]: SOURCE_DOT_HEX.document,
};

/**
 * The data-source tab a manifest opens on. There is no user-facing "primary"
 * concept: we default to the manifest's `primarySource` when it's available,
 * then the first available source, then e-Manifest — but the user only ever
 * sees and selects concrete sources.
 */
export function defaultSource(
  primarySource: WasteManifestSource | null | undefined,
  availableSources: readonly WasteManifestSource[]
): WasteManifestSource {
  if (primarySource && availableSources.includes(primarySource)) return primarySource;
  const firstAvailable = SOURCE_ORDER.find((s) => availableSources.includes(s));
  return firstAvailable ?? availableSources[0] ?? WasteManifestSource.EManifest;
}

export interface ResolvedFieldValue {
  value: string | null | undefined;
  /** False when the selected source carries no value for this field. */
  present: boolean;
}

/**
 * Pick a field's value for a concrete source from a per-source value map. A
 * source is "present" only when it is in `availableSources` AND has a value.
 */
export function pickBySource(
  source: WasteManifestSource,
  availableSources: readonly WasteManifestSource[],
  values: Partial<Record<WasteManifestSource, string | null | undefined>>
): ResolvedFieldValue {
  const value = values[source];
  const present = availableSources.includes(source) && value != null && value !== "";
  return { value: present ? value : undefined, present };
}
